import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Clock, Lock } from 'lucide-react'
import { apiClient } from '@/services/api.client'
import { useAuthStore } from '@/stores/auth.store'

type AbonelikDurum = {
  durum: string
  grace?: boolean
  saltOkunur?: boolean
  gunKaldi?: number
  graceGunKaldi?: number
  bitisTarihi?: string
}

/**
 * Header'da abonelik yaşam döngüsü rozeti:
 *  - saltOkunur (ek süre doldu) → kırmızı "Abonelik bitti · salt-okunur"
 *  - grace (ek süre, tam erişim) → turuncu "Ek süre: X gün"
 *  - bitişe ≤7 gün → sarı "Abonelik: X gün"
 * Tıklayınca abonelik/yenileme ekranına gider.
 */
export function AbonelikDurumRozet() {
  const kullanici = useAuthStore((s) => s.kullanici)
  // super_admin abonelikten muaf; oturum yoksa sorgulama
  const aktif = !!kullanici && kullanici.rol !== 'super_admin'

  const { data } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () =>
      (apiClient.get('/subscription/status') as Promise<{ data: AbonelikDurum }>).then((r) => r.data),
    enabled: aktif,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  if (!data) return null

  let tip: 'salt' | 'grace' | 'yaklasiyor' | null = null
  if (data.saltOkunur) tip = 'salt'
  else if (data.grace) tip = 'grace'
  else if ((data.durum === 'aktif' || data.durum === 'deneme') && (data.gunKaldi ?? 99) <= 7 && (data.gunKaldi ?? 0) > 0)
    tip = 'yaklasiyor'

  if (!tip) return null

  const stil =
    tip === 'salt'
      ? 'bg-error/10 border-error/30 text-error'
      : tip === 'grace'
        ? 'bg-warning/15 border-warning/40 text-warning'
        : 'bg-warning/10 border-warning/30 text-warning'

  const tarihStr = data.bitisTarihi ? new Date(data.bitisTarihi).toLocaleDateString('tr-TR') : ''
  const metin =
    tip === 'salt'
      ? 'Abonelik bitti · salt-okunur'
      : tip === 'grace'
        ? `Ek süre: ${Math.max(0, data.graceGunKaldi ?? 0)} gün`
        : `Bitiş ${tarihStr} · ${data.gunKaldi} gün`

  const baslik =
    tip === 'salt'
      ? 'Aboneliğiniz sona erdi (ek süre de doldu). Hesap salt-okunur — yeni kayıt/değişiklik yapılamaz. Yenilemek için tıklayın.'
      : tip === 'grace'
        ? 'Aboneliğiniz sona erdi; ek süre boyunca tüm özellikler açık. Süre bitmeden yenileyin.'
        : 'Aboneliğinizin bitişine az kaldı. Kesintisiz kullanım için yenileyin.'

  return (
    <Link
      to="/ayarlar/abonelik"
      title={baslik}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium whitespace-nowrap hover:opacity-80 transition-opacity ${stil}`}
    >
      {tip === 'salt' ? <Lock className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      <span className="hidden md:inline">{metin}</span>
      <span className="md:hidden">{tip === 'salt' ? 'Bitti' : `${tip === 'grace' ? data.graceGunKaldi : data.gunKaldi}g`}</span>
    </Link>
  )
}
